/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: ObjectBaseComponent (object view) of one pd.PdPartner (Datenaustausch-Partner) — the receiving
 *         company whose contracts are watched for storno, storno-date change, reinstatement and payment
 *         suspension. Left navigation with „Übersicht“ (metadata form + processing-lock panel), „Ereignisse“
 *         (the partner's contract-event ledger), „Sammelläufe“ and „Exportläufe“; an action bar starts
 *         collection / full / dry runs and exports, sets up the job rows and releases a stuck lock
 *         (requirement pd-contract-events §8.1–§8.3, endpoints §7.3).
 */
import {Component, Inject, LOCALE_ID, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {Observable, Subscription} from 'rxjs';
import {ObjectBaseComponent} from 'features/core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {ObserverService} from 'features/core/shared/object/service/observer.service';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetForm} from 'features/core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from 'features/core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from 'features/core/shared/widget/widget-config-form-field';
import {
    WidgetRuntimeFilterSortingEnum
} from 'features/core/shared/widget/component/runtime/implementations/mvs-widget-table/enum/widget-runtime-filter-sorting.enum';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {EntityStatusEnum} from 'features/core/shared/entity/enum/entity-status.enum';
import {PdPartnerDto} from '../../../model/dto/entity/pd-partner.dto';
import {PdCollectionModeEnum} from '../../../model/dto/enum/pd-collection-mode.enum';
import {PdEventTypeEnum} from '../../../model/dto/enum/pd-event-type.enum';
import {PdProcessingKindEnum} from '../../../model/dto/enum/pd-processing-kind.enum';
import {PdPartnerJobStartResultDto} from '../../../model/dto/api/pd-partner-job-start-result.dto';
import {PdPartnerProvisionJobsResultDto} from '../../../model/dto/api/pd-partner-provision-jobs-result.dto';
import {PdPartnerReleaseLockResultDto} from '../../../model/dto/api/pd-partner-release-lock-result.dto';
import {PdPartnerService} from '../../../service/api/pd-partner.service';
import {
    pdFormatDateTime,
    pdFormatLocalDate,
    pdIsLocked,
    pdProcessingKindLabel,
    pdProcessingStatusLabel,
    pdProcessingStatusTone,
    pdStatusChipClass
} from '../../../logic/pd-status-display';

/** The actions of the action bar; at most one runs at a time. */
type PdPartnerAction = 'collection' | 'full' | 'dryRun' | 'export' | 'provision' | 'release';

/** A row value handed to a {@code #cust_} template; the shape differs between the table variants. */
type PdRowRecord = Record<string, unknown>;
type PdCustomCellObject = { row?: { dto?: PdRowRecord } & PdRowRecord; dto?: PdRowRecord } & PdRowRecord;

/**
 * Object component of {@code pd.PdPartner}, registered for update mode in {@code pd.entity-provider.ts} (create mode
 * stays on the generic metadata form).
 *
 * <h3>Data</h3>
 * The partner itself comes from the framework ({@code ObjectBaseComponent} loads it through PdPartnerService). The
 * three lists are curated QL widget tables ({@code createWidgetTableQl} + fixed {@code widgetForm}), fixed-filtered on
 * {@code e.pdPartnerId}. They are built ONCE per partner id: a refresh of the partner (after an action or a save) must
 * not throw away the user's sorting, header filters and page — the mounted table is reloaded in place instead
 * ({@link reloadMountedTable}).
 *
 * <h3>Actions (requirement §7.3)</h3>
 * Every start is asynchronous on the server: the answer arrives before the {@code jb.JbJobRun} exists and before the
 * job has taken the partner's lock. The partner and the visible table are therefore reloaded at once AND once more
 * {@link FOLLOW_UP_REFRESH_MS} later, so the new run and the lock show up without a manual reload.
 *
 * The action calls opt out of the global error toast ({@code SUPPRESS_ERROR_HANDLER} in PdPartnerService), so this
 * component owns every toast: refusals are HTTP 400 with a German text ({@code CoreApiResponseDto.data}), shown as
 * an error toast; 409 gets a German conflict hint, anything else a generic German error text.
 *
 * <h3>Lock (DC-12) and inactive partner</h3>
 * Locked means exactly {@code processingStatus == PROCESSING}. While locked, the start buttons are disabled and
 * „Sperre lösen“ is offered; the server refuses the release while jb still reports an active execution (DC-3).
 * A partner whose {@code entityStatus} is not {@code active} cannot be started either (the launcher refuses it); the
 * header says „Inaktiv“ and a hint names the reason.
 */
@Component({
    selector: 'mvs-pd-partner-object-component',
    templateUrl: './pd-partner-object.component.html',
    styleUrls: ['./pd-partner-object.component.scss'],
    standalone: false
})
export class PdPartnerObjectComponent
    extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    /** Second reload after a start: the job takes the lock and writes its run record asynchronously. */
    private static readonly FOLLOW_UP_REFRESH_MS = 4000;

    private static readonly OBJECT_TYPE_EVENT = 'pd.PdPartnerContractEvent';
    private static readonly OBJECT_TYPE_COLLECTION_RUN = 'pd.PdPartnerCollectionRun';
    private static readonly OBJECT_TYPE_EXPORT_RUN = 'pd.PdPartnerExportRun';

    /** {@code MvsWidgetCustomFieldDirective.fieldPrefix} — a layout field with it renders a host template. */
    private static readonly CUSTOM_FIELD_PREFIX = '#cust_';

    /** Instant columns as „dd.MM.yyyy HH:mm“ — without it an Instant renders as „vor 2 Stunden“. */
    private static readonly OUTPUT_DATE_TIME = 'dateTime';

    /** The {@code #cust_} columns of the tables, matched by the template's custom-field directive. */
    readonly customColumns = {
        eventPayload: {fieldId: 'pdEventPayload', label: 'Ereignisdaten'},
        eventSupersedes: {fieldId: 'pdEventSupersedes', label: 'Ersetzt Ereignis'},
        runMessage: {fieldId: 'pdRunMessage', label: 'Meldung'},
    } as const;

    declare dto: PdPartnerDto;

    navigationItems: NavigationItem[] = [
        {
            label: 'Übersicht',
            action: 'overview',
            tooltip: 'Stammdaten, Verarbeitungsstatus und letzte Läufe des Partners',
            icon: 'fa-regular fa-circle-info',
            toggleable: false,
            default: true
        },
        {
            label: 'Ereignisse',
            action: 'events',
            tooltip: 'Vertragsereignisse des Partners – offen, überholt und versendet',
            icon: 'fa-regular fa-list-check',
            toggleable: false,
            default: false
        },
        {
            label: 'Sammelläufe',
            action: 'collectionRuns',
            tooltip: 'Sammelläufe, die die Vertragsereignisse des Partners ermittelt haben',
            icon: 'fa-regular fa-magnifying-glass',
            toggleable: false,
            default: false
        },
        {
            label: 'Exportläufe',
            action: 'exportRuns',
            tooltip: 'Exportläufe, die die offenen Ereignisse an den Partner ausgegeben haben',
            icon: 'fa-regular fa-paper-plane',
            toggleable: false,
            default: false
        }
    ];

    eventsWidget: WidgetData;
    collectionRunsWidget: WidgetData;
    exportRunsWidget: WidgetData;

    /** Only the table of the active section is mounted; the other two are undefined. */
    @ViewChild('eventsWidgetRef') eventsWidgetComponent: MvsWidgetComponent | undefined;
    @ViewChild('collectionRunsWidgetRef') collectionRunsWidgetComponent: MvsWidgetComponent | undefined;
    @ViewChild('exportRunsWidgetRef') exportRunsWidgetComponent: MvsWidgetComponent | undefined;

    /** The action currently waiting for its answer; every action button is disabled meanwhile. */
    runningAction: PdPartnerAction | null = null;

    /** Partner id the tables were built for — they are rebuilt only when the partner changes. */
    private widgetsPartnerId: number | null = null;

    private queryParamSubscription: Subscription;
    private followUpRefreshTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        private activatedRoute: ActivatedRoute,
        private partnerService: PdPartnerService,
        private navigationService: MvsObjectNavigationService,
        @Inject(LOCALE_ID) private locale: string
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscribeToNavItemQueryParam();
    }

    override onObjectChanged(): void {
        this.setPageTitle(this.dto?.alias ? `Datenaustausch-Partner ${this.dto.alias}` : 'Datenaustausch-Partner');

        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems[0];
        }

        // A refresh of the same partner keeps the tables (sorting, header filters, page) and the navigation.
        if (this.widgetsPartnerId !== this.dto?.id) {
            this.widgetsPartnerId = this.dto?.id ?? null;
            this.onNavigationItems.emit(this.navigationItems);
            this.createWidgets();
        }
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        this.queryParamSubscription?.unsubscribe();
        this.clearFollowUpRefresh();
        super.ngOnDestroy();
    }

    // =================================================================================================================
    // Header state
    // =================================================================================================================

    /** Locked = a job holds the partner (DC-12: exactly PROCESSING; QUEUED and FAILED are not locked). */
    get isLocked(): boolean {
        return pdIsLocked(this.dto?.processingStatus);
    }

    /**
     * Inactive = an {@code entityStatus} other than {@code active}; the launcher refuses to start such a partner and
     * scheduled runs skip it. A missing status is not treated as inactive — the server decides then.
     */
    get isInactive(): boolean {
        const status = this.dto?.entityStatus;
        return status != null && status !== EntityStatusEnum.active;
    }

    /** The start buttons wait for an active partner, the lock and the answer of a running action. */
    get startDisabled(): boolean {
        return this.isInactive || this.isLocked || this.runningAction !== null;
    }

    get processingStatusLabel(): string {
        return pdProcessingStatusLabel(this.dto?.processingStatus);
    }

    get processingStatusChipClass(): string {
        return pdStatusChipClass(pdProcessingStatusTone(this.dto?.processingStatus));
    }

    get inactiveChipClass(): string {
        return pdStatusChipClass('neutral');
    }

    /** German hint shown while the partner is locked: who holds it and since when. */
    get lockHint(): string {
        const kindLabel = pdProcessingKindLabel(this.dto?.processingKind);
        const holder = kindLabel ? `ein ${kindLabel}` : 'eine Verarbeitung';
        const since = pdFormatDateTime(this.dto?.processingStartedAt, this.locale);
        return `Für den Partner läuft gerade ${holder}${since ? ` (gesperrt seit ${since})` : ''}. `
            + 'Sammellauf, Vollabgleich, Probelauf und Export sind gesperrt, bis die Verarbeitung endet. '
            + '„Sperre lösen“ ist nur für eine hängende Sperre gedacht – solange der Joblauf noch aktiv ist, '
            + 'lehnt der Server ab.';
    }

    /** German hint shown while the partner is inactive. */
    get inactiveHint(): string {
        return 'Der Partner ist nicht aktiv. Sammellauf, Vollabgleich, Probelauf und Export können nicht gestartet '
            + 'werden, und geplante Läufe überspringen ihn. Setzen Sie den Status in den Stammdaten auf aktiv, um ihn '
            + 'wieder zu verarbeiten.';
    }

    // =================================================================================================================
    // Actions
    // =================================================================================================================

    /** „Sammellauf starten“ — regular delta collection. */
    startCollection(): void {
        this.runAction('collection', 'Sammellauf',
            this.partnerService.startCollection(this.dto.id, PdCollectionModeEnum.DELTA, false),
            result => this.showStarted('Sammellauf', result, 'Der Sammellauf wurde gestartet.'), true);
    }

    /** „Vollabgleich starten“ — full comparison of all candidate contracts (E-11). */
    startFullCollection(): void {
        this.runAction('full', 'Vollabgleich',
            this.partnerService.startCollection(this.dto.id, PdCollectionModeEnum.FULL, false),
            result => this.showStarted('Vollabgleich', result, 'Der Vollabgleich wurde gestartet.'), true);
    }

    /** „Probelauf“ — delta collection that counts only and writes nothing (E-11). */
    startDryRun(): void {
        this.runAction('dryRun', 'Probelauf',
            this.partnerService.startCollection(this.dto.id, PdCollectionModeEnum.DELTA, true),
            result => this.showStarted('Probelauf', result, 'Der Probelauf wurde gestartet.'), true);
    }

    /** „Export starten“ — hands the pending events to the partner's export channel. */
    startExport(): void {
        this.runAction('export', 'Export',
            this.partnerService.startExport(this.dto.id),
            result => this.showStarted('Export', result, 'Der Export wurde gestartet.'), true);
    }

    /** „Jobs einrichten“ — creates the two job rows (draft), idempotent. */
    provisionJobs(): void {
        this.runAction('provision', 'Jobs einrichten',
            this.partnerService.provisionJobs(this.dto.id),
            result => this.showProvisioned(result), false);
    }

    /** „Sperre lösen“ — asks first; the release itself is {@link releaseLock}. */
    confirmReleaseLock(): void {
        this.confirmationService.confirm({
            header: 'Sperre lösen',
            message: `Die Sperre des Partners „${this.dto.alias}“ wird freigegeben. Das ist nur für eine hängende `
                + 'Sperre gedacht: Läuft der sperrende Joblauf noch, lehnt der Server ab. Möchten Sie fortfahren?',
            icon: 'fa-regular fa-triangle-exclamation',
            acceptLabel: 'Sperre lösen',
            rejectLabel: 'Abbrechen',
            acceptButtonProps: {severity: 'danger'},
            rejectButtonProps: {severity: 'secondary', outlined: true},
            accept: () => this.releaseLock()
        });
    }

    /** „Aktualisieren“ — reloads the partner and the visible table. */
    refreshView(): void {
        this.refreshPartnerAndTables();
    }

    private releaseLock(): void {
        this.runAction('release', 'Sperre lösen',
            this.partnerService.releaseLock(this.dto.id),
            result => this.showReleased(result), false);
    }

    /**
     * Runs one action: {@code onSuccess} shows the server's German answer, a refusal its German error text; either
     * way the partner and the visible table are reloaded afterwards.
     *
     * @param followUp reload once more after {@link FOLLOW_UP_REFRESH_MS} (asynchronous job starts)
     */
    private runAction<T>(
        action: PdPartnerAction,
        title: string,
        call$: Observable<T>,
        onSuccess: (result: T) => void,
        followUp: boolean
    ): void {
        if (this.runningAction !== null) {
            return;
        }
        this.runningAction = action;

        call$.subscribe({
            next: (result: T) => {
                this.runningAction = null;
                onSuccess(result);
                this.refreshPartnerAndTables();
                if (followUp) {
                    this.scheduleFollowUpRefresh();
                }
            },
            error: (error: HttpErrorResponse) => {
                this.runningAction = null;
                this.showActionError(title, error);
                this.refreshPartnerAndTables();
            }
        });
    }

    /** A start was accepted: the server's German confirmation (the run itself starts asynchronously). */
    private showStarted(title: string, result: PdPartnerJobStartResultDto, fallbackMessage: string): void {
        this.messageService.showSuccessMessage(title, result?.message || fallbackMessage);
    }

    /**
     * The job rows were set up. A row the server could not set up (no {@code jobTypeId}, e.g. duplicate rows) turns
     * the toast into a warning that names that row's German reason.
     */
    private showProvisioned(result: PdPartnerProvisionJobsResultDto): void {
        const title = 'Jobs einrichten';
        const message = result?.message || 'Die Jobs des Partners wurden eingerichtet.';
        const failedJobs = (result?.jobs ?? []).filter(job => job.jobTypeId == null);

        if (failedJobs.length > 0) {
            const reasons = failedJobs.map(job => job.message).filter(reason => !!reason).join(' ');
            this.messageService.showWarningMessage(title, reasons ? `${message} ${reasons}` : message);
        } else {
            this.messageService.showSuccessMessage(title, message);
        }
    }

    /**
     * The lock was released — or there was none: {@code released = false} is still HTTP 200 and is shown as an info
     * toast. A released lock names the job that held it.
     */
    private showReleased(result: PdPartnerReleaseLockResultDto): void {
        const title = 'Sperre lösen';
        if (result?.released === false) {
            this.messageService.showInfoMessage(title, result.message || 'Der Partner war nicht gesperrt.');
            return;
        }
        const holder = result?.previousKind === PdProcessingKindEnum.EXPORT ? 'den Export'
            : result?.previousKind === PdProcessingKindEnum.COLLECTION ? 'den Sammellauf'
                : null;
        const fallback = holder ? `Die Sperre durch ${holder} wurde gelöst.` : 'Die Sperre wurde gelöst.';
        this.messageService.showSuccessMessage(title, result?.message || fallback);
    }

    /** 400 = refusal with the server's German text, 409 = conflict hint, everything else = generic German text. */
    private showActionError(title: string, error: HttpErrorResponse): void {
        if (error?.status === 400) {
            this.messageService.showErrorMessage(title, PdPartnerObjectComponent.serverText(error)
                ?? 'Die Aktion wurde vom Server abgelehnt.');
        } else if (error?.status === 409) {
            this.messageService.showWarningMessage(title, PdPartnerObjectComponent.serverText(error)
                ?? 'Der Partner wurde zwischenzeitlich von einem anderen Vorgang geändert, z. B. von einem laufenden '
                + 'Job. Die Ansicht wurde neu geladen – bitte prüfen Sie den Stand und versuchen Sie es erneut.');
        } else {
            this.messageService.showErrorMessage(title,
                'Die Aktion konnte nicht ausgeführt werden. Bitte versuchen Sie es später erneut.');
        }
    }

    /** The German text of a refusal: {@code CoreApiResponseDto.data}, else {@code message}, else a plain-text body. */
    private static serverText(error: HttpErrorResponse): string | null {
        const body: unknown = error?.error;
        if (typeof body === 'string') {
            return body.trim() ? body : null;
        }
        const record = body as { data?: unknown; message?: unknown } | null;
        if (typeof record?.data === 'string' && record.data.trim()) {
            return record.data;
        }
        if (typeof record?.message === 'string' && record.message.trim()) {
            return record.message;
        }
        return null;
    }

    private refreshPartnerAndTables(): void {
        this.refreshObject(false);
        this.reloadMountedTable();
    }

    /** Reloads the visible table IN PLACE — same widget, same sorting, filters and page. */
    private reloadMountedTable(): void {
        this.eventsWidgetComponent?.refreshWidget();
        this.collectionRunsWidgetComponent?.refreshWidget();
        this.exportRunsWidgetComponent?.refreshWidget();
    }

    private scheduleFollowUpRefresh(): void {
        this.clearFollowUpRefresh();
        this.followUpRefreshTimer = setTimeout(() => {
            this.followUpRefreshTimer = null;
            this.refreshPartnerAndTables();
        }, PdPartnerObjectComponent.FOLLOW_UP_REFRESH_MS);
    }

    private clearFollowUpRefresh(): void {
        if (this.followUpRefreshTimer !== null) {
            clearTimeout(this.followUpRefreshTimer);
            this.followUpRefreshTimer = null;
        }
    }

    // =================================================================================================================
    // Master-data form
    // =================================================================================================================

    /**
     * Unsaved edits in the metadata form make the object component dirty, so the host can warn before the user
     * navigates away; a save or reset of the form clears it again.
     */
    handlePartnerFormDirty(dirty: boolean): void {
        if (dirty) {
            this.markAsDirty();
        } else if (this.isDirty) {
            this.isDirty = false;
            this.onComponentDirty.emit(false);
        }
    }

    /** The metadata form saved or soft-deleted the partner — reload it so header and lock panel follow. */
    handlePartnerFormChanged(event: ObjectChangeInformation): void {
        switch (event?.action) {
            case ObjectChangeInformationActionEnum.updated:
            case ObjectChangeInformationActionEnum.deleted:
                this.refreshObject(false);
                break;
            case ObjectChangeInformationActionEnum.created:
                // The form is bound to an existing partner; a create cannot originate here.
                break;
        }
    }

    // =================================================================================================================
    // Table rows → generic object view (read-only: the backend refuses every write on events and runs)
    // =================================================================================================================

    handleEventSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        this.openObject(PdPartnerObjectComponent.OBJECT_TYPE_EVENT, PdPartnerObjectComponent.selectedId(event));
    }

    handleCollectionRunSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        this.openObject(PdPartnerObjectComponent.OBJECT_TYPE_COLLECTION_RUN,
            PdPartnerObjectComponent.selectedId(event));
    }

    handleExportRunSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        this.openObject(PdPartnerObjectComponent.OBJECT_TYPE_EXPORT_RUN, PdPartnerObjectComponent.selectedId(event));
    }

    /** „Ersetzt Ereignis“ cell: opens the superseded event; the click must not also select the row. */
    openSupersededEvent(object: unknown, clickEvent: MouseEvent): void {
        clickEvent?.stopPropagation();
        this.openObject(PdPartnerObjectComponent.OBJECT_TYPE_EVENT,
            PdPartnerObjectComponent.toId(PdPartnerObjectComponent.rowValue(object, 'sup.id')));
    }

    private openObject(objectType: string, objectId: number | null): void {
        if (objectId == null) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            objectType, objectId, 'object', '', '', '', MvsObjectNavigationActionEnum.display);
        this.navigationService.navigateTo(entry, 'right');
    }

    /** Id of a selected QL row: the emitted objectId, else the raw row's {@code e.id}. Longs may arrive as strings. */
    private static selectedId(event: ObjectIdentifierData | ObjectIdentifier): number | null {
        const row = (event as ObjectIdentifierData)?.data as PdRowRecord | undefined;
        return PdPartnerObjectComponent.toId(event?.objectId ?? row?.['e.id']);
    }

    private static toId(value: unknown): number | null {
        const id = Number(value);
        return value != null && Number.isFinite(id) && id > 0 ? id : null;
    }

    // =================================================================================================================
    // #cust_ cells
    // =================================================================================================================

    /** Id of the superseded event of a row, or null. */
    supersededEventId(object: unknown): number | null {
        return PdPartnerObjectComponent.toId(PdPartnerObjectComponent.rowValue(object, 'sup.id'));
    }

    /** The event's payload in one line, worded by event type (dates DD.MM.YYYY). */
    eventPayloadText(object: unknown): string {
        const value = (key: string) => PdPartnerObjectComponent.rowValue(object, key);
        const date = (key: string) => pdFormatLocalDate(value(key), this.locale);
        const period = () => [date('e.suspensionStartDate'), date('e.suspensionEndDate')]
            .filter(part => !!part).join(' – ');

        // Number(null) would read as 0 = STORNO.
        const eventType = value('e.eventType');
        if (eventType == null || eventType === '') {
            return '';
        }

        switch (Number(eventType)) {
            case PdEventTypeEnum.STORNO:
                return date('e.terminationDate') ? `Storno zum ${date('e.terminationDate')}` : '';
            case PdEventTypeEnum.STORNO_DATE_CHANGE:
                return `Stornodatum ${date('e.terminationDate') || '–'} → ${date('e.newTerminationDate') || '–'}`;
            case PdEventTypeEnum.REINSTATEMENT: {
                const effective = date('e.reinstatementDate') || date('e.terminationDate');
                return effective ? `Wieder in Kraft zum ${effective}` : '';
            }
            case PdEventTypeEnum.PAYMENT_SUSPENSION:
            case PdEventTypeEnum.PAYMENT_SUSPENSION_CHANGE:
                return period() ? `Ausgesetzt ${period()}` : '';
            case PdEventTypeEnum.PAYMENT_SUSPENSION_CANCELLATION:
                return period() ? `Aufgehoben: ${period()}` : '';
            default:
                return '';
        }
    }

    /** Run message of a row ('' when empty); the cell shows one line, the tooltip the full text. */
    runMessage(object: unknown): string {
        const message = PdPartnerObjectComponent.rowValue(object, 'e.message');
        return typeof message === 'string' ? message : '';
    }

    /**
     * One value of the source row a custom-field template was handed: {@code object.row.dto} is the unmodified QL
     * row with every projected key; the fallbacks cover the other table shapes (same helper as the bd workbenches).
     */
    private static rowValue(object: unknown, key: string): unknown {
        const cell = object as PdCustomCellObject | null | undefined;
        return cell?.row?.dto?.[key]
            ?? cell?.row?.[key]
            ?? cell?.dto?.[key]
            ?? cell?.[key]
            ?? null;
    }

    // =================================================================================================================
    // Navigation
    // =================================================================================================================

    /**
     * Deep link into one section via {@code ?navItem=} — {@code ObjectBaseComponent} does not read it on its own
     * (fe_core_object-base-component G6.5).
     */
    private subscribeToNavItemQueryParam(): void {
        this.queryParamSubscription = this.activatedRoute.queryParams.subscribe(params => {
            const navItemAction = params['navItem'];
            if (navItemAction && this.navigationItems) {
                const found = this.navigationItems.find(item => item.action === navItemAction);
                if (found) {
                    this.activeNavigationItems = found;
                }
            }
        });
    }

    // =================================================================================================================
    // Widgets
    // =================================================================================================================

    private createWidgets(): void {
        const partnerId = this.dto?.id;
        if (partnerId == null) {
            return;
        }
        this.eventsWidget = this.createContractEventsWidget(partnerId);
        this.collectionRunsWidget = this.createCollectionRunsWidget(partnerId);
        this.exportRunsWidget = this.createExportRunsWidget(partnerId);
    }

    /**
     * Ledger of the partner's contract events (§8.3): newest first, VSNR from the joined contract, the superseded
     * event joined for the clickable „Ersetzt Ereignis“ cell. Status, Ereignistyp and VSNR are filtered through the
     * table's header filters — no own filter controls. Column set validated with build-table-widget.js; the hidden
     * {@code sup} join and the payload fields read by {@code #cust_pdEventPayload} are added by hand.
     */
    private createContractEventsWidget(partnerId: number): WidgetData {
        const alias = 'pd.partner.object.contractEvent.table';
        const dateTime = PdPartnerObjectComponent.OUTPUT_DATE_TIME;

        const qlQuery: QlQueryDto = {
            name: alias,
            type: 'view',
            start: {
                name: 'pd.PdPartnerContractEvent',
                as: 'e',
                type: 'entity',
                fields: [
                    {name: 'id'},
                    {name: 'eventType'},            // Ereignistyp (also read by #cust_pdEventPayload)
                    {name: 'status'},               // Status
                    {name: 'terminationDate'},      // read by #cust_pdEventPayload
                    {name: 'newTerminationDate'},   // read by #cust_pdEventPayload
                    {name: 'reinstatementDate'},    // read by #cust_pdEventPayload
                    {name: 'suspensionStartDate'},  // read by #cust_pdEventPayload
                    {name: 'suspensionEndDate'},    // read by #cust_pdEventPayload
                    {name: 'obsoleteReason'},       // Überholungsgrund
                    {name: 'changeTimestamp'},      // Änderungszeitpunkt
                    {name: 'sentAt'},               // Versendet am
                    {name: 'createdDate'},          // Angelegt am
                ],
                joins: [
                    {
                        type: 'entity', name: 'contract', as: 'c', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'contractIdentifier'}],   // VSNR
                    },
                    {
                        type: 'entity', name: 'supersedesEvent', as: 'sup', joinType: 'left',
                        fields: [{name: 'id'}],                                 // read by #cust_pdEventSupersedes
                    },
                ],
            },
            fixedFilters: [
                FilterCriteria.create('e.pdPartnerId', FilterCriteria.cOperatorEqual, partnerId),
            ],
            // Newest first; the id tiebreaker keeps events of the same collection run in a stable order.
            sortings: [
                new Sorting('e.createdDate', false),
                new Sorting('e.id', false),
            ],
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: pd.PdPartnerContractEvent ledger of ONE Datenaustausch-Partner — curated QL table with VSNR,
         *         event type, status, payload, obsolete reason and the clickable superseded event; header filters
         *         on status / event type / VSNR, a row opens the event in the generic object view.
         */
        const widget = WidgetFactory.createWidgetTableQl(
            alias,
            'Vertragsereignisse',
            qlQuery,
            WidgetDataParam.create('widgetForm', this.buildWidgetForm([
                {field: 'c.contractIdentifier', uiLabel: 'VSNR'},
                {field: 'e.eventType', uiLabel: 'Ereignistyp'},
                {field: 'e.status', uiLabel: 'Status'},
                this.customColumn(this.customColumns.eventPayload),
                {field: 'e.obsoleteReason', uiLabel: 'Überholungsgrund'},
                this.customColumn(this.customColumns.eventSupersedes),
                {field: 'e.changeTimestamp', uiLabel: 'Änderungszeitpunkt', output: dateTime},
                {field: 'e.sentAt', uiLabel: 'Versendet am', output: dateTime},
                {field: 'e.createdDate', uiLabel: 'Angelegt am', output: dateTime},
            ])),
            // The header filters ARE the optional filters of §8.3 (status, event type, contract).
            WidgetDataParam.create('filter', WidgetRuntimeFilterSortingEnum.header),
            WidgetDataParam.create('selectionMode', 'rowSelect'),
        );
        widget.noDataText = 'Für diesen Partner gibt es noch keine Vertragsereignisse. '
            + 'Sie entstehen durch einen Sammellauf.';
        return widget;
    }

    /** Collection runs of the partner (§8.3), newest first. Column set validated with build-table-widget.js. */
    private createCollectionRunsWidget(partnerId: number): WidgetData {
        const alias = 'pd.partner.object.collectionRun.table';
        const dateTime = PdPartnerObjectComponent.OUTPUT_DATE_TIME;

        const qlQuery: QlQueryDto = {
            name: alias,
            type: 'view',
            start: {
                name: 'pd.PdPartnerCollectionRun',
                as: 'e',
                type: 'entity',
                fields: [
                    {name: 'id'},
                    {name: 'startedAt'},
                    {name: 'finishedAt'},
                    {name: 'status'},
                    {name: 'mode'},
                    {name: 'dryRun'},
                    {name: 'contractsChecked'},
                    {name: 'contractsChanged'},
                    {name: 'eventsCreated'},
                    {name: 'eventsObsoleted'},
                    {name: 'eventsUnchanged'},
                    {name: 'warnings'},
                    {name: 'message'},              // read by #cust_pdRunMessage
                ],
            },
            fixedFilters: [
                FilterCriteria.create('e.pdPartnerId', FilterCriteria.cOperatorEqual, partnerId),
            ],
            sortings: [
                new Sorting('e.startedAt', false),
                new Sorting('e.id', false),
            ],
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: pd.PdPartnerCollectionRun list of ONE Datenaustausch-Partner — curated QL table with time, status,
         *         mode, dry-run flag, the counters and the run message; a row opens the run in the generic object view.
         */
        const widget = WidgetFactory.createWidgetTableQl(
            alias,
            'Sammelläufe',
            qlQuery,
            WidgetDataParam.create('widgetForm', this.buildWidgetForm([
                {field: 'e.startedAt', uiLabel: 'Gestartet am', output: dateTime},
                {field: 'e.finishedAt', uiLabel: 'Beendet am', output: dateTime},
                {field: 'e.status', uiLabel: 'Status'},
                {field: 'e.mode', uiLabel: 'Laufmodus'},
                {field: 'e.dryRun', uiLabel: 'Probelauf'},
                {field: 'e.contractsChecked', uiLabel: 'Geprüfte Verträge'},
                {field: 'e.contractsChanged', uiLabel: 'Geänderte Verträge'},
                {field: 'e.eventsCreated', uiLabel: 'Angelegte Ereignisse'},
                {field: 'e.eventsObsoleted', uiLabel: 'Überholte Ereignisse'},
                {field: 'e.eventsUnchanged', uiLabel: 'Unveränderte Ereignisse'},
                {field: 'e.warnings', uiLabel: 'Warnungen'},
                this.customColumn(this.customColumns.runMessage),
            ])),
            WidgetDataParam.create('selectionMode', 'rowSelect'),
        );
        widget.noDataText = 'Für diesen Partner ist noch kein Sammellauf gelaufen. '
            + 'Starten Sie einen Probelauf oder einen Sammellauf über die Aktionsleiste.';
        return widget;
    }

    /** Export runs of the partner (§8.3), newest first. Column set validated with build-table-widget.js. */
    private createExportRunsWidget(partnerId: number): WidgetData {
        const alias = 'pd.partner.object.exportRun.table';
        const dateTime = PdPartnerObjectComponent.OUTPUT_DATE_TIME;

        const qlQuery: QlQueryDto = {
            name: alias,
            type: 'view',
            start: {
                name: 'pd.PdPartnerExportRun',
                as: 'e',
                type: 'entity',
                fields: [
                    {name: 'id'},
                    {name: 'startedAt'},
                    {name: 'finishedAt'},
                    {name: 'status'},
                    {name: 'channel'},
                    {name: 'eventsSelected'},
                    {name: 'eventsSent'},
                    {name: 'eventsFailed'},
                    {name: 'channelReference'},
                    {name: 'message'},              // read by #cust_pdRunMessage
                ],
            },
            fixedFilters: [
                FilterCriteria.create('e.pdPartnerId', FilterCriteria.cOperatorEqual, partnerId),
            ],
            sortings: [
                new Sorting('e.startedAt', false),
                new Sorting('e.id', false),
            ],
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: pd.PdPartnerExportRun list of ONE Datenaustausch-Partner — curated QL table with time, status,
         *         channel, selected / sent / failed counts, channel reference and run message; a row opens the run
         *         in the generic object view.
         */
        const widget = WidgetFactory.createWidgetTableQl(
            alias,
            'Exportläufe',
            qlQuery,
            WidgetDataParam.create('widgetForm', this.buildWidgetForm([
                {field: 'e.startedAt', uiLabel: 'Gestartet am', output: dateTime},
                {field: 'e.finishedAt', uiLabel: 'Beendet am', output: dateTime},
                {field: 'e.status', uiLabel: 'Status'},
                {field: 'e.channel', uiLabel: 'Kanal'},
                {field: 'e.eventsSelected', uiLabel: 'Ausgewählte Ereignisse'},
                {field: 'e.eventsSent', uiLabel: 'Versendete Ereignisse'},
                {field: 'e.eventsFailed', uiLabel: 'Fehlgeschlagene Ereignisse'},
                {field: 'e.channelReference', uiLabel: 'Kanalreferenz'},
                this.customColumn(this.customColumns.runMessage),
            ])),
            WidgetDataParam.create('selectionMode', 'rowSelect'),
        );
        widget.noDataText = 'Für diesen Partner ist noch kein Export gelaufen. '
            + 'Ein Export gibt die offenen Ereignisse über den Exportkanal aus.';
        return widget;
    }

    private customColumn(column: { fieldId: string; label: string }): { field: string; uiLabel: string } {
        return {field: PdPartnerObjectComponent.CUSTOM_FIELD_PREFIX + column.fieldId, uiLabel: column.label};
    }

    /**
     * Curated column set + order + German headers, passed as the {@code widgetForm} param. A stored ui.UiWidgetLayout
     * for the same alias replaces it outright and must then keep the {@code #cust_} columns, or their cells disappear.
     */
    private buildWidgetForm(columns: { field: string; uiLabel: string; output?: string }[]): WidgetForm {
        const layout = new WidgetConfigFormLayout();
        // hideFields=true is the default-layout convention: only the configured fields are shown.
        layout.hideFields = true;
        layout.fieldsConfigured = true;

        columns.forEach((column, index) => {
            const field = WidgetConfigFormField.create(column.field, '', '');
            field.uiLabel = column.uiLabel;
            field.uiPriority = index;
            if (column.output) {
                field.uiOutputField = column.output;
            }
            layout.addField(field);
        });

        const widgetForm = new WidgetForm();
        widgetForm.setDefaultLayout(layout);
        return widgetForm;
    }
}
